import _defineProperty from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/runner/_work/instore-core/instore-core/node_modules/gatsby/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["state", "hideOnClick", "preventScrollOnKeyDown", "focusOnHover"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { getPopupItemRole } from 'ariakit-utils/dom';
import { isOpeningInNewTab } from 'ariakit-utils/events';
import { useBooleanEvent, useEvent } from 'ariakit-utils/hooks';
import { useStore, createMemoComponent } from 'ariakit-utils/store';
import { createHook, createElement } from 'ariakit-utils/system';
import { useCompositeHover } from '../composite/composite-hover.js';
import { useCompositeItem } from '../composite/composite-item.js';
import { a as MenuBarContext, M as MenuContext, h as hasExpandedMenuButton } from '../__utils-07f4a93f.js';
/**
 * A component hook that returns props that can be passed to `Role` or any other
 * Ariakit component to render a menu item.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const state = useMenuState();
 * const undo = useMenuItem({ state });
 * const redo = useMenuItem({ state });
 * <MenuButton state={state}>Edit</MenuButton>
 * <Menu state={state}>
 *   <Role {...undo}>Undo</Role>
 *   <Role {...redo}>Redo</Role>
 * </Menu>
 * ```
 */

var useMenuItem = createHook(function (_ref) {
  var state = _ref.state,
      _ref$hideOnClick = _ref.hideOnClick,
      hideOnClick = _ref$hideOnClick === void 0 ? true : _ref$hideOnClick,
      _ref$preventScrollOnK = _ref.preventScrollOnKeyDown,
      preventScrollOnKeyDown = _ref$preventScrollOnK === void 0 ? true : _ref$preventScrollOnK,
      _focusOnHover = _ref.focusOnHover,
      props = _objectWithoutProperties(_ref, _excluded); // Use MenuBar state as a fallback.


  var menuBarState = useStore(state || MenuBarContext, ["items"]);
  state = useStore(state || MenuContext, ["move", "hideAll", "contentElement"]) || menuBarState;
  var onClickProp = props.onClick;
  var hideOnClickProp = useBooleanEvent(hideOnClick);
  var hideMenu = state && "hideAll" in state ? state.hideAll : undefined;
  var isWithinMenu = !!hideMenu;
  var onClick = useEvent(function (event) {
    onClickProp == null ? void 0 : onClickProp(event);
    if (event.defaultPrevented) return;
    if (isOpeningInNewTab(event)) return;
    if (!hideMenu) return; // If this item is also a menu button, we don't want to hide the menu.

    var popupType = event.currentTarget.getAttribute("aria-haspopup");
    if (popupType === "menu") return;
    if (!hideOnClickProp(event)) return;
    hideMenu();
  });
  var role = getPopupItemRole(state && "contentElement" in state ? state.contentElement : null, "menuitem");
  props = _objectSpread(_objectSpread({
    role: role
  }, props), {}, {
    onClick: onClick
  });
  props = useCompositeItem(_objectSpread({
    state: state,
    preventScrollOnKeyDown: preventScrollOnKeyDown
  }, props));
  props = useCompositeHover(_objectSpread(_objectSpread({
    state: state
  }, props), {}, {
    focusOnHover: function focusOnHover(event) {
      var _state;

      if (typeof _focusOnHover === "function") return _focusOnHover(event);
      if (_focusOnHover != null) return _focusOnHover; // The menu container should be focused on mouseleave only if the menu
      // item is inside a menu, not a menu bar.

      if (event.type === "mouseleave") return isWithinMenu;

      if (isWithinMenu) {
        // If the menu item is also a submenu button, we should move actual
        // DOM focus to it so that the submenu will not close when the user
        // moves the cursor back to the menu button.
        if (event.currentTarget.hasAttribute("aria-expanded")) {
          event.currentTarget.focus();
        }

        return true;
      } // If the menu item is inside a menu bar, we should move DOM focus to
      // the menu item if there's another expanded menu button inside the menu
      // bar. Without this, the open menus in the menu bar wouldn't close.
      else if (hasExpandedMenuButton((_state = state) == null ? void 0 : _state.items, event.currentTarget)) {
        event.currentTarget.focus();
        return true;
      }

      return false;
    }
  }));
  return props;
});
/**
 * A component that renders a menu item.
 * @see https://ariakit.org/components/menu
 * @example
 * ```jsx
 * const menu = useMenuState();
 * <MenuButton state={menu}>Edit</MenuButton>
 * <Menu state={menu}>
 *   <MenuItem>Undo</MenuItem>
 *   <MenuItem>Redo</MenuItem>
 * </Menu>
 * ```
 */

var MenuItem = createMemoComponent(function (props) {
  var htmlProps = useMenuItem(props);
  return createElement("div", htmlProps);
});
export { MenuItem, useMenuItem };